import { Fragment, useEffect, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router';

import { Combobox, Menu, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, MapPinIcon } from '@heroicons/react/24/outline';

import { useCart } from '@boc/framework/checkout/cart/provider';
import {
  type CountryData,
  getChannelForCountry,
  getCountriesData,
  getCountryData,
  getSupportedCountriesFromChannels,
} from '@boc/framework/countries';
import { CountryCode } from '@boc/framework/saleor-graphql';

import { cn } from '~/styling';
import { parseParams, urls } from '~/urls';

import { channels } from '#/channels';

// Get country data for supported countries

const NOTIFICATION_COOLDOWN_KEY = 'jsn-country-notification-dismissed';
const COOLDOWN_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export default function CountrySelector() {
  const { country, setCountry, cart, detectedCountry, isCartLoaded } = useCart();

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { channel: currentChannel } = parseParams(params);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState<'first-visit' | 'unsupported' | null>(
    null
  );
  const [query, setQuery] = useState('');

  const countries = getCountriesData(getSupportedCountriesFromChannels(channels)).sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const selectedCountry = countries.find((c) => c.code === country) || countries[0];
  
  // Create a list of unique channels for display
  const channelOptions = Object.values(channels).map(ch => ({
    channel: ch,
    displayName: ch.name,
    countries: countries.filter(c => getChannelForCountry(c.code, channels) === ch.slug)
  }));
  
  const useSearchable = channelOptions.length > 5;

  const filteredOptions =
    query === ''
      ? channelOptions
      : channelOptions.filter((option) => option.displayName.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    // Don't run until cart has been loaded to avoid hydration mismatches
    if (!isCartLoaded) return;
    if (!detectedCountry) return;

    // Don't show if checkout exists (country is already selected)
    if (cart?.id) return;

    // Check if notification was recently dismissed
    const dismissedTime = localStorage.getItem(NOTIFICATION_COOLDOWN_KEY);

    if (dismissedTime) {
      const timeSinceDismissed = Date.now() - parseInt(dismissedTime, 10);

      if (timeSinceDismissed < COOLDOWN_DURATION) return;
    }

    // Don't show notification if detected country matches current country
    if (detectedCountry === country) return;

    // Check if detected country is supported
    const isSupported = countries.some((c) => c.code === detectedCountry);

    if (!isSupported) {
      // Show "we don't ship to X" notification
      setNotificationType('unsupported');
      setShowNotification(true);
    } else {
      // Show "shopping from X" notification when detected country differs from current country
      setNotificationType('first-visit');
      setShowNotification(true);
    }
  }, [country, cart?.id, isCartLoaded, detectedCountry, countries]);

  const handleChannelChange = (targetChannel: typeof channels[keyof typeof channels]) => {
    // Update the country in cart context to the channel's default country
    setCountry(targetChannel.defaultCountry);

    if (targetChannel.urlSlug !== currentChannel.urlSlug) {
      // Reload the page with the new channel
      const currentPath = location.pathname;
      const newPath = currentPath.replace(
        `/${currentChannel.urlSlug}/`,
        `/${targetChannel.urlSlug}/`
      );

      window.location.href = newPath + location.search;
    }
  };

  const handleCountryChange = (newCountryOrCode: CountryCode | CountryData) => {
    const newCountryCode =
      typeof newCountryOrCode === 'string' ? newCountryOrCode : newCountryOrCode.code;

    // Update the country in cart context
    setCountry(newCountryCode);

    // Get the appropriate channel for the country
    const channelSlug = getChannelForCountry(newCountryCode, channels);
    const newChannel = Object.values(channels).find((ch) => ch.slug === channelSlug);

    if (newChannel && newChannel.urlSlug !== currentChannel.urlSlug) {
      // Reload the page with the new channel
      const currentPath = location.pathname;
      const newPath = currentPath.replace(
        `/${currentChannel.urlSlug}/`,
        `/${newChannel.urlSlug}/`
      );

      window.location.href = newPath + location.search;
    }
  };

  const handleDismissNotification = () => {
    setShowNotification(false);
    localStorage.setItem(NOTIFICATION_COOLDOWN_KEY, Date.now().toString());
  };

  const getNotificationContent = () => {
    if (notificationType === 'first-visit') {
      const countryName = detectedCountry ? getCountryData(detectedCountry).name : 'your location';

      return (
        <>
          <p className="text-primary-2 font-medium">
            Looks like you&apos;re shopping from{' '}
            <span className="font-semibold">{countryName}</span>!
          </p>
          <p className="text-primary-2-80 mt-1">
            Need a different country? Pick one from the dropdown.
          </p>
        </>
      );
    }

    if (notificationType === 'unsupported') {
      const countryName = detectedCountry ? getCountryData(detectedCountry).name : 'your location';

      return (
        <p className="text-primary-2 font-medium">
          Oh no, we don&apos;t ship to <span className="font-semibold">{countryName}</span> just
          yet, but we&apos;re hoping to reach you soon!
        </p>
      );
    }

    return null;
  };

  return (
    <div className="relative">
      {useSearchable ? (
        // Searchable combobox for more than 5 countries
        <Combobox value={selectedCountry} onChange={(selection) => {
          // If selection has a channel property, we're selecting a channel not a country
          if ('channel' in selection && selection.channel) {
            handleChannelChange(selection.channel);
          } else {
            handleCountryChange(selection);
          }
        }}>
          <div className="relative">
            <Combobox.Button
              className={cn(
                'text-primary-2-80 flex items-center gap-1.5 transition-colors',
                'hover:text-primary-2-100 focus:outline-none'
              )}
            >
              <MapPinIcon className="hidden h-4 w-4 sm:block" />
              <span className="hidden text-sm sm:inline">Deliver to {currentChannel.name}</span>
              <span className="text-lg sm:hidden">{selectedCountry.flag}</span>
              <ChevronDownIcon className="h-3 w-3" />
            </Combobox.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Combobox.Options className="bg-primary-2-10 absolute top-full right-0 z-50 mt-2 w-72 rounded-md py-1 shadow-lg focus:outline-none sm:right-auto sm:left-0">
                <div className="border-primary-2-20 border-b px-3 py-2">
                  <Combobox.Input
                    className="border-primary-2-40 text-primary-2 placeholder-primary-2-60 focus:border-primary-2-60 w-full rounded border bg-white px-3 py-1.5 text-sm focus:outline-none"
                    placeholder="Search regions..."
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </div>

                <div className="max-h-60 overflow-auto py-1">
                  {filteredOptions.length === 0 && query !== '' ? (
                    <div className="text-primary-2-60 px-4 py-2 text-sm">No regions found.</div>
                  ) : (
                    filteredOptions.map((option) => (
                      <Combobox.Option
                        key={option.channel.slug}
                        value={{ channel: option.channel }}
                        className={({ active }) =>
                          cn(
                            'relative cursor-pointer py-2 pr-4 pl-4 text-sm select-none',
                            active ? 'bg-primary-2-20 text-primary-2' : 'text-primary-2'
                          )
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={cn('block truncate', selected && 'font-medium')}>
                              {option.displayName}
                            </span>
                            {selected && (
                              <span className="text-primary-2-60 absolute inset-y-0 right-2 flex items-center">
                                <CheckIcon className="h-4 w-4" />
                              </span>
                            )}
                          </>
                        )}
                      </Combobox.Option>
                    ))
                  )}
                </div>
              </Combobox.Options>
            </Transition>
          </div>
        </Combobox>
      ) : (
        // Simple menu for 5 or fewer countries
        <Menu as="div" className="static inline-block">
          {({ open }) => (
            <>
              <Menu.Button
                className={cn(
                  'text-primary-2-80 flex items-center gap-1.5 transition-colors',
                  'hover:text-primary-2-100 focus:outline-none'
                )}
              >
                <MapPinIcon className="hidden h-4 w-4 sm:block" />
                <span className="hidden text-sm sm:inline">Deliver to {currentChannel.name}</span>
                <span className="text-lg sm:hidden">{selectedCountry.flag}</span>
                <ChevronDownIcon
                  className={cn('h-3 w-3 transition-transform duration-200', open && 'rotate-180')}
                />
              </Menu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="bg-primary-2-10 absolute top-full right-0 z-50 mt-2 w-full min-w-[200px] rounded-md py-2 shadow-lg focus:outline-none sm:right-auto sm:left-0">
                  {channelOptions.map((option) => {
                    const isSelected = option.channel.slug === currentChannel.slug;
                    return (
                      <Menu.Item key={option.channel.slug}>
                        {({ active }) => (
                          <button
                            className={cn(
                              'text-primary-2 flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors',
                              active && 'bg-primary-2-20',
                              isSelected && 'font-medium'
                            )}
                            onClick={() => handleChannelChange(option.channel)}
                          >
                            <span>{option.displayName}</span>
                          </button>
                        )}
                      </Menu.Item>
                    );
                  })}
                </Menu.Items>
              </Transition>
            </>
          )}
        </Menu>
      )}

      {/* Notification appears below the country selector */}
      {showNotification && (
        <div className="fixed top-14 right-0 left-0 z-[200] sm:absolute sm:top-full sm:right-0 sm:left-auto sm:mt-3">
          <Transition
            show={showNotification}
            enter="transition-all duration-300 ease-out"
            enterFrom="transform -translate-y-2 opacity-0"
            enterTo="transform translate-y-0 opacity-100"
            leave="transition-all duration-200 ease-in"
            leaveFrom="transform translate-y-0 opacity-100"
            leaveTo="transform -translate-y-2 opacity-0"
          >
            <div className="relative">
              {/* Arrow pointer - hidden on mobile */}
              <div className="border-b-primary-2-20 absolute -top-2 right-8 hidden h-0 w-0 transform border-r-[8px] border-b-[8px] border-l-[8px] border-r-transparent border-l-transparent sm:block" />

              {/* Notification content */}
              <div
                className={cn(
                  'bg-primary-2-20 shadow-lg',
                  'px-4 py-3 sm:rounded-lg sm:px-6 sm:py-4',
                  'w-full sm:w-[500px]'
                )}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 text-sm">{getNotificationContent()}</div>
                  <button
                    onClick={handleDismissNotification}
                    className={cn(
                      'ml-4 flex-shrink-0',
                      'border-primary-2-60 rounded-full border px-4 py-1.5',
                      'text-primary-2 text-xs font-medium uppercase',
                      'hover:bg-primary-2-40 transition-colors hover:text-white',
                      'focus:ring-primary-2-60 focus:ring-2 focus:outline-none'
                    )}
                  >
                    DISMISS
                  </button>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      )}
    </div>
  );
}
